import { flushSync } from "react-dom";
import { Location } from "../../types/map";
import { dispatchMapEvent, MAP_EVENTS } from "@/utils/map/mapEventBus";

/** Same breakpoint as the map menu: full-screen overlay vs side drawer. */
export const MOBILE_MAP_MENU_MAX_WIDTH_PX = 768;

/** Use at interaction time (click/tap), not only from React state — `isMobile` can lag one frame after load/hydration. */
export function isNarrowMapMenuViewport(): boolean {
  return (
    typeof window !== "undefined" &&
    window.innerWidth <= MOBILE_MAP_MENU_MAX_WIDTH_PX
  );
}

export type NavigateToLocationOptions = {
  /** When true, skip map flyTo (e.g. mobile uses bottom-sheet camera offset via zoomToClickedLocation). */
  skipFlyTo?: boolean;
  /**
   * When true, always close the map menu before navigating.
   * When false, never close (e.g. desktop side panel).
   * When omitted, close only if `isNarrowMapMenuViewport()` (wider phones/tablets skip and the
   * menu could incorrectly stay open over the map).
   */
  closeMenu?: boolean;
};

export type NavigateToMultipleLocationsOptions = {
  closeMenu?: boolean;
  applyListFilter?: boolean;
  listName?: string;
  urlPath?: string;
};

// Navigate to a single location
export const navigateToLocation = (
  location: Location,
  mapRef: any,
  mapState: any,
  navigate: any,
  options?: NavigateToLocationOptions,
) => {
  const shouldCloseMenu =
    typeof options?.closeMenu === "boolean"
      ? options.closeMenu
      : isNarrowMapMenuViewport();

  if (shouldCloseMenu) {
    flushSync(() => {
      mapState.handleCloseLeftsideMenu?.();
    });
    // LoginButton mounts a second MapMenu driven by `showUserMenu`; "View more data" opens both.
    if (typeof window !== "undefined") {
      dispatchMapEvent(MAP_EVENTS.closeUserMenu);
    }
  }

  mapState.clearUserLocationPin();
  mapState.clearListFilter();

  mapState.updateDataStates({
    eventPreviewPin: null,
    gbifHexDetailAnchor: null,
    selectedGbifHexGeometry: null,
    gbifOccurrenceTotal: null,
    userLocationPin: {
      name: location.name,
      description: location.description,
      latitude: location.latitude,
      longitude: location.longitude,
      species: location.species,
    },
  });

  navigate(`/maps?lat=${location.latitude}&lng=${location.longitude}&zoom=14`);

  if (!options?.skipFlyTo && mapRef.current) {
    mapRef.current.flyTo({
      center: [location.longitude, location.latitude],
      zoom: 14,
      duration: 2000,
      essential: true,
    });
  }
};

// Navigate to multiple locations (fit bounds)
export const navigateToMultipleLocations = (
  locations: Location[],
  mapRef: any,
  mapState: any,
  navigate: any,
  options?: NavigateToMultipleLocationsOptions,
) => {
  if (locations.length === 0) return;

  if (locations.length === 1) {
    navigateToLocation(locations[0], mapRef, mapState, navigate, {
      closeMenu: options?.closeMenu,
    });
    return;
  }

  const shouldCloseMenu =
    typeof options?.closeMenu === "boolean"
      ? options.closeMenu
      : isNarrowMapMenuViewport();

  if (shouldCloseMenu) {
    flushSync(() => {
      mapState.handleCloseLeftsideMenu?.();
    });
    if (typeof window !== "undefined") {
      dispatchMapEvent(MAP_EVENTS.closeUserMenu);
    }
  }

  mapState.clearUserLocationPin();
  mapState.updateDataStates({ eventPreviewPin: null });
  const applyListFilter = options?.applyListFilter !== false;
  const listId = applyListFilter ? locations[0].listId : null;
  mapState.setSelectedListForMap(listId || null);

  const rawListName =
    options?.listName || (locations[0] as any).listName || "List";
  const listName = rawListName.endsWith("List")
    ? rawListName
    : `${rawListName} List`;
  mapState.setCurrentListName(listName);

  const lats = locations.map((loc) => loc.latitude);
  const lngs = locations.map((loc) => loc.longitude);

  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);

  const latPadding = (maxLat - minLat) * 0.1;
  const lngPadding = (maxLng - minLng) * 0.1;

  const bounds = [
    [minLng - lngPadding, minLat - latPadding],
    [maxLng + lngPadding, maxLat + latPadding],
  ];

  if (mapRef.current) {
    mapRef.current.fitBounds(bounds as [[number, number], [number, number]], {
      padding: 50,
      duration: 2000,
      essential: true,
    });
  }

  const targetPath = options?.urlPath
    ? options.urlPath
    : `/maps/saved/${listName
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")}`;
  navigate(targetPath);
};

// Zoom to clicked location with tooltip offset
export const zoomToClickedLocation = (
  clickLocation: { lng: number; lat: number },
  mapRef: any,
  mapState: any,
) => {
  if (!mapRef.current) return;

  const map = mapRef.current.getMap();
  const container = map.getContainer();
  const containerHeight = container.offsetHeight;

  // Shift the map so the clicked pin appears between the search bar and
  // the bottom sheet without moving it off-screen. We translate a fixed
  // pixel offset into degrees based on the current view.
  const tooltipHeight = 80; // approximate combined height of drawer + padding
  const pinOffset = 0;
  const totalOffset = tooltipHeight + pinOffset;

  const bounds = map.getBounds();
  const pixelsPerDegree =
    containerHeight / (bounds.getNorth() - bounds.getSouth());
  const latOffset = totalOffset / pixelsPerDegree;

  const adjustedLat = clickLocation.lat - latOffset;

  const currentZoom =
    typeof map.getZoom === "function"
      ? map.getZoom()
      : mapState?.viewState?.zoom ?? 10;

  mapRef.current.flyTo({
    center: [clickLocation.lng, adjustedLat],
    zoom: Math.min(currentZoom + 1, 14),
    duration: 1200,
  });
};

// Zoom to state bounds
export const zoomToState = (mapRef: any) => {
  if (!mapRef.current) return;

  const map = mapRef.current.getMap();
  const bounds = map.getBounds();

  map.fitBounds(
    [
      [bounds.getWest(), bounds.getSouth()],
      [bounds.getEast(), bounds.getNorth()],
    ],
    {
      padding: 50,
      duration: 1500,
    },
  );
};

/** County + state for map tooltip under coordinates. */
export function formatCountyStateLine(loc: {
  county?: string;
  state?: string;
}): string {
  const c = loc.county?.trim();
  const s = loc.state?.trim();
  if (c && s) return `${c}, ${s}`;
  if (c) return c;
  if (s) return s;
  return "";
}
